import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { announcementsService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
    HiOutlineSpeakerphone,
    HiOutlineClock,
    HiOutlineOfficeBuilding,
    HiOutlineMail,
    HiOutlinePhone,
    HiOutlinePencil,
    HiOutlineTrash,
    HiOutlineDocument,
    HiOutlinePlus,
    HiOutlineX,
    HiOutlineCheckCircle,
    HiOutlineInformationCircle
} from 'react-icons/hi';

const MEDIA_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5001/api').replace(/\/api$/, '');

const AnnouncementsManage = () => {
    const [announcements, setAnnouncements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingAnnouncement, setEditingAnnouncement] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [documentPreview, setDocumentPreview] = useState(null);
    useAuth();
    
    const [formData, setFormData] = useState({
        title_es: '',
        description_es: '',
        deadline: '',
        featured_image: null,
        document_url: null,
        organization: '',
        contact_email: '',
        contact_phone: '',
        registration_url: '',
        requirements: '',
        benefits: '',
        is_active: true
    });

    useEffect(() => {
        loadAnnouncements();
    }, []);

    const loadAnnouncements = async () => {
        try {
            const response = await announcementsService.getAll();
            setAnnouncements(response.data);
        } catch (error) {
            toast.error('Error al cargar convocatorias');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleFileChange = (e) => {
        const { name, files } = e.target;
        if (files && files[0]) {
            setFormData(prev => ({
                ...prev,
                [name]: files[0]
            }));

            if (name === 'featured_image' && files[0].type.startsWith('image/')) {
                const reader = new FileReader();
                reader.onloadend = () => {
                    setImagePreview(reader.result);
                };
                reader.readAsDataURL(files[0]);
            }

            if (name === 'document_url') {
                setDocumentPreview(files[0].name);
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const submitData = new FormData();
            
            Object.keys(formData).forEach(key => {
                if (key !== 'featured_image' && key !== 'document_url' && formData[key] !== null && formData[key] !== '') {
                    submitData.append(key, formData[key]);
                }
            });

            if (formData.featured_image) {
                submitData.append('featured_image', formData.featured_image);
            }
            if (formData.document_url) {
                submitData.append('document_url', formData.document_url);
            }

            if (editingAnnouncement) {
                await announcementsService.update(editingAnnouncement.id, submitData);
                toast.success('Convocatoria actualizada exitosamente');
            } else {
                await announcementsService.create(submitData);
                toast.success('Convocatoria creada exitosamente');
            }

            resetForm();
            loadAnnouncements();
            setShowModal(false);
        } catch (error) {
            toast.error(error.response?.data?.error || 'Error al guardar convocatoria');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (announcement) => {
        setEditingAnnouncement(announcement);
        setFormData({
            title_es: announcement.title_es,
            description_es: announcement.description_es,
            deadline: announcement.deadline?.split('T')[0] || '',
            featured_image: null,
            document_url: null,
            organization: announcement.organization || '',
            contact_email: announcement.contact_email || '',
            contact_phone: announcement.contact_phone || '',
            registration_url: announcement.registration_url || '',
            requirements: announcement.requirements || '',
            benefits: announcement.benefits || '',
            is_active: announcement.is_active
        });
        
        if (announcement.featured_image) {
            setImagePreview(`${MEDIA_URL}${announcement.featured_image}`);
        }
        if (announcement.document_url) {
            setDocumentPreview(announcement.document_url);
        }
        
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('¿Estás seguro de eliminar esta convocatoria?')) return;

        try {
            await announcementsService.delete(id);
            toast.success('Convocatoria eliminada');
            loadAnnouncements();
        } catch (error) {
            toast.error('Error al eliminar convocatoria');
            console.error(error);
        }
    };

    const resetForm = () => {
        setFormData({
            title_es: '',
            description_es: '',
            deadline: '',
            featured_image: null,
            document_url: null,
            organization: '',
            contact_email: '',
            contact_phone: '',
            registration_url: '',
            requirements: '',
            benefits: '',
            is_active: true
        });
        setEditingAnnouncement(null);
        setImagePreview(null);
        setDocumentPreview(null);
    };

    const getStatusBadge = (deadline) => {
        const today = new Date();
        const deadlineDate = new Date(deadline);
        const daysLeft = Math.ceil((deadlineDate - today) / (1000 * 60 * 60 * 24));
        
        if (daysLeft < 0) {
            return <span className="badge badge-warning">Vencida</span>;
        } else if (daysLeft <= 7) {
            return <span className="badge badge-success">Por vencer ({daysLeft}d)</span>;
        } else {
            return <span className="badge badge-success">Vigente</span>;
        }
    };

    if (loading && announcements.length === 0) {
        return (
            <div className="loading-container">
                <div className="spinner"></div>
                <p>Cargando convocatorias...</p>
            </div>
        );
    }

    return (
        <div className="content-manage">
            <div className="content-header">
                <div className="header-info">
                    <h1>Gestión de Convocatorias</h1>
                    <p>Crea y administra las convocatorias de la federación</p>
                </div>
                <button className="btn btn-primary" onClick={() => {
                    resetForm();
                    setShowModal(true);
                }}>
                    <HiOutlinePlus />
                    <span>Nueva Convocatoria</span>
                </button>
            </div>

            {announcements.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-state-icon">
                        <HiOutlineSpeakerphone />
                    </div>
                    <h3>No hay convocatorias registradas</h3>
                    <p>Comienza creando tu primera convocatoria</p>
                    <button className="btn btn-primary" onClick={() => {
                        resetForm();
                        setShowModal(true);
                    }}>
                        <HiOutlinePlus />
                        <span>Crear Convocatoria</span>
                    </button>
                </div>
            ) : (
                <div className="events-grid">
                    {announcements.map(announcement => (
                        <div key={announcement.id} className="event-card card">
                            {announcement.featured_image && (
                                <div className="event-image-container">
                                    <img 
                                        src={`${MEDIA_URL}${announcement.featured_image}`} 
                                        alt={announcement.title_es}
                                        className="event-image"
                                    />
                                </div>
                            )}
                            <div className="card-body">
                                <div className="event-header-row">
                                    <h3 className="event-title">{announcement.title_es}</h3>
                                    {announcement.deadline && getStatusBadge(announcement.deadline)}
                                </div>
                                <p className="event-description">{announcement.description_es}</p>
                                
                                <div className="event-details">
                                    {announcement.deadline && (
                                        <div className="detail-item">
                                            <HiOutlineClock />
                                            <span>Fecha límite: {new Date(announcement.deadline).toLocaleDateString('es-ES', { 
                                                day: '2-digit', 
                                                month: 'long', 
                                                year: 'numeric' 
                                            })}</span>
                                        </div>
                                    )}
                                    {announcement.organization && (
                                        <div className="detail-item">
                                            <HiOutlineOfficeBuilding />
                                            <span>{announcement.organization}</span>
                                        </div>
                                    )}
                                    {announcement.contact_email && (
                                        <div className="detail-item">
                                            <HiOutlineMail />
                                            <span>{announcement.contact_email}</span>
                                        </div>
                                    )}
                                    {announcement.contact_phone && (
                                        <div className="detail-item">
                                            <HiOutlinePhone />
                                            <span>{announcement.contact_phone}</span>
                                        </div>
                                    )}
                                </div>

                                <div className="event-actions">
                                    <button className="btn btn-sm btn-secondary" onClick={() => handleEdit(announcement)}>
                                        <HiOutlinePencil />
                                        <span>Editar</span>
                                    </button>
                                    <button className="btn btn-sm btn-danger" onClick={() => handleDelete(announcement.id)}>
                                        <HiOutlineTrash />
                                        <span>Eliminar</span>
                                    </button>
                                    {announcement.document_url && (
                                        <a 
                                            href={`${MEDIA_URL}${announcement.document_url}`} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="btn btn-sm btn-secondary"
                                        >
                                            <HiOutlineDocument />
                                            <span>Ver Documento</span>
                                        </a>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {showModal && (
                <div className="modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="modal" style={{ maxWidth: '800px' }} onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2 className="modal-title">
                                {editingAnnouncement ? 'Editar Convocatoria' : 'Nueva Convocatoria'}
                            </h2>
                            <button className="modal-close" onClick={() => setShowModal(false)}>
                                <HiOutlineX />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit}>
                            <div className="modal-body">
                                <div className="alert-info">
                                    <HiOutlineInformationCircle />
                                    <span>Solo escribe en español. La traducción al inglés se hará automáticamente.</span>
                                </div>

                                <div className="grid grid-cols-2">
                                    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                                        <label className="form-label">Título (Español) *</label>
                                        <input
                                            type="text"
                                            name="title_es"
                                            className="form-control"
                                            value={formData.title_es}
                                            onChange={handleInputChange}
                                            required
                                            placeholder="Ej: Convocatoria para Entrenadores Nacionales 2026"
                                        />
                                    </div>

                                    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                                        <label className="form-label">Descripción (Español) *</label>
                                        <textarea
                                            name="description_es"
                                            className="form-control"
                                            value={formData.description_es}
                                            onChange={handleInputChange}
                                            required
                                            rows="4"
                                            placeholder="Describe la convocatoria..."
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">Fecha Límite *</label>
                                        <input
                                            type="date"
                                            name="deadline"
                                            className="form-control"
                                            value={formData.deadline}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">Organización</label>
                                        <input
                                            type="text"
                                            name="organization"
                                            className="form-control"
                                            value={formData.organization}
                                            onChange={handleInputChange}
                                            placeholder="Ej: Federación Mexicana de Natación"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">Email de Contacto</label>
                                        <input
                                            type="email"
                                            name="contact_email"
                                            className="form-control"
                                            value={formData.contact_email}
                                            onChange={handleInputChange}
                                            placeholder="contacto@ejemplo.com"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">Teléfono de Contacto</label>
                                        <input
                                            type="tel"
                                            name="contact_phone"
                                            className="form-control"
                                            value={formData.contact_phone}
                                            onChange={handleInputChange}
                                            placeholder="+52 55 1234 5678"
                                        />
                                    </div>

                                    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                                        <label className="form-label">URL de Registro</label>
                                        <input
                                            type="url"
                                            name="registration_url"
                                            className="form-control"
                                            value={formData.registration_url}
                                            onChange={handleInputChange}
                                            placeholder="https://..."
                                        />
                                    </div>

                                    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                                        <label className="form-label">Requisitos</label>
                                        <textarea
                                            name="requirements"
                                            className="form-control"
                                            value={formData.requirements}
                                            onChange={handleInputChange}
                                            rows="3"
                                            placeholder="Lista los requisitos separados por comas o saltos de línea"
                                        />
                                    </div>

                                    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                                        <label className="form-label">Beneficios</label>
                                        <textarea
                                            name="benefits"
                                            className="form-control"
                                            value={formData.benefits}
                                            onChange={handleInputChange}
                                            rows="3"
                                            placeholder="Lista los beneficios separados por comas o saltos de línea"
                                        />
                                    </div>

                                    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                                        <label className="form-label">Imagen Destacada</label>
                                        <div className="file-input-wrapper">
                                            <input
                                                type="file"
                                                name="featured_image"
                                                className="file-input"
                                                onChange={handleFileChange}
                                                accept="image/*"
                                                id="announcement-image-input"
                                            />
                                            <label htmlFor="announcement-image-input" className="file-input-label">
                                                <HiOutlineDocument />
                                                <span>Seleccionar imagen de la convocatoria</span>
                                            </label>
                                        </div>
                                        {imagePreview && (
                                            <div className="image-preview">
                                                <img src={imagePreview} alt="Preview" />
                                            </div>
                                        )}
                                    </div>

                                    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                                        <label className="form-label">Documento PDF/Archivo</label>
                                        <div className="file-input-wrapper">
                                            <input
                                                type="file"
                                                name="document_url"
                                                className="file-input"
                                                onChange={handleFileChange}
                                                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                                id="announcement-document-input"
                                            />
                                            <label htmlFor="announcement-document-input" className="file-input-label">
                                                <HiOutlineDocument />
                                                <span>Seleccionar documento (PDF, Word, Imagen)</span>
                                            </label>
                                        </div>
                                        {documentPreview && (
                                            <div className="file-preview">
                                                <HiOutlineDocument />
                                                <span>{documentPreview}</span>
                                            </div>
                                        )}
                                    </div>

                                    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                                        <label className="form-check">
                                            <input
                                                type="checkbox"
                                                name="is_active"
                                                className="form-check-input"
                                                checked={formData.is_active}
                                                onChange={handleInputChange}
                                            />
                                            <span>Convocatoria activa (visible en el sitio)</span>
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                                    Cancelar
                                </button>
                                <button type="submit" className="btn btn-primary" disabled={loading}>
                                    {loading ? (
                                        <>
                                            <div className="spinner" style={{ width: '16px', height: '16px', borderWidth: '2px' }}></div>
                                            <span>Guardando...</span>
                                        </>
                                    ) : (
                                        <>
                                            <HiOutlineCheckCircle />
                                            <span>{editingAnnouncement ? 'Actualizar' : 'Crear Convocatoria'}</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AnnouncementsManage;
